/**
 * Global Loading State Context for TECDocChart
 * 
 * Provides centralized loading state management across the application.
 * Replaces the AngularJS loading controller with React Context.
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';

interface LoadingState {
  isLoading: boolean;
  loadingMessage?: string;
  loadingId?: string;
}

interface LoadingContextValue {
  /** Current loading state */
  loading: LoadingState;
  
  /** Show loading with optional message and ID */
  showLoading: (message?: string, id?: string) => void;
  
  /** Hide loading by ID or hide all if no ID provided */
  hideLoading: (id?: string) => void;
  
  /** Check if specific loading ID is active */
  isLoadingId: (id: string) => boolean;
  
  /** Toggle loading state */
  toggleLoading: (message?: string, id?: string) => void;
}

const LoadingContext = createContext<LoadingContextValue | undefined>(undefined);

interface LoadingProviderProps {
  children: ReactNode;
}

/**
 * Loading Context Provider component
 */
export const LoadingProvider: React.FC<LoadingProviderProps> = ({ children }) => {
  const [loading, setLoading] = useState<LoadingState>({
    isLoading: false
  });

  const [loadingStack, setLoadingStack] = useState<Set<string>>(new Set());

  const showLoading = useCallback((message?: string, id: string = 'default') => {
    setLoadingStack(prev => new Set(prev).add(id));
    setLoading({
      isLoading: true,
      loadingMessage: message,
      loadingId: id
    });
  }, []);

  const hideLoading = useCallback((id?: string) => {
    if (id) {
      setLoadingStack(prev => {
        const newStack = new Set(prev);
        newStack.delete(id);
        
        // If no more loading operations, hide loading
        if (newStack.size === 0) {
          setLoading({ isLoading: false });
        }
        
        return newStack;
      });
    } else {
      // Hide all loading
      setLoadingStack(new Set());
      setLoading({ isLoading: false });
    }
  }, []);

  const isLoadingId = useCallback((id: string) => {
    return loadingStack.has(id);
  }, [loadingStack]);

  const toggleLoading = useCallback((message?: string, id: string = 'default') => {
    if (isLoadingId(id)) {
      hideLoading(id);
    } else {
      showLoading(message, id);
    }
  }, [isLoadingId, hideLoading, showLoading]);

  const contextValue: LoadingContextValue = {
    loading,
    showLoading,
    hideLoading,
    isLoadingId,
    toggleLoading
  };

  return (
    <LoadingContext.Provider value={contextValue}>
      {children}
    </LoadingContext.Provider>
  );
};

/**
 * Hook to use loading context
 */
export const useLoading = (): LoadingContextValue => {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

/**
 * Higher-order component to add loading capabilities
 */
export function withLoading<T extends object>(
  WrappedComponent: React.ComponentType<T>
): React.ComponentType<T> {
  return function WithLoadingComponent(props: T) {
    const loading = useLoading();
    return <WrappedComponent {...props} loading={loading} />;
  };
}

/**
 * Hook for API calls with automatic loading states
 */
export const useLoadingAPI = () => {
  const { showLoading, hideLoading } = useLoading();

  const withLoading = useCallback(async <T,>(
    apiCall: () => Promise<T>,
    message?: string,
    id?: string
  ): Promise<T> => {
    const loadingId = id || `api-${Date.now()}`;
    
    try {
      showLoading(message, loadingId);
      const result = await apiCall();
      return result;
    } finally {
      hideLoading(loadingId);
    }
  }, [showLoading, hideLoading]);

  return { withLoading };
};

export default LoadingContext;